/**
 * Copyright (c) 2025 Robert Schiwon
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { isSameUrl } from 'util/UrlUtils';
import CustomEventsMessageBus from 'util/CustomEventsMessageBus';
// import * as Plotly from 'plotly.js';
import * as Plotly from 'plotly.js-dist-min';
import './DataChart.css';
const template = '<div id="sf-data-chart-placeholder"/>';
const nodeSelectedEvent = 'sf-tree-node-selected';
const nodeDeselectedEvent = 'sf-tree-node-deselected';
const nodeDataUpdatedEvent = 'sf-tree-node-data-updated';
export default class DataChart extends HTMLElement {
    #initialized = false;
    #channel = CustomEventsMessageBus.getDefaultChannel();
    #handles = [];
    #url = null;
    #chartContainer = null;
    #chartState = {
        data: {
            x: [],
            y: [],
            // alternatively, use scattergl for potentially better performance
            type: 'scatter',
            mode: 'lines',
            line: {
                width: 1,
            },
        },
        layout: {
            // no title
            // smaller margins
            margin: {
                l: 80,
                r: 30,
                b: 80,
                t: 30,
                pad: 10,
            },
            xaxis: {
                title: {
                    text: '',
                },
                showgrid: true,
                zeroline: false,
                showline: true,
                autorange: true,
            },
            yaxis: {
                title: {
                    text: '',
                },
                showgrid: true,
                zeroline: false,
                showline: true,
            },
        },
        config: {
            responsive: true,
            displaylogo: false,
        },
    };
    get data() {
        const x = this.#chartState.data.x;
        const y = this.#chartState.data.y;
        const xyData = DataChart.fromXyArrays({ x, y });
        // xaxis/yaxis.title properties do not return a string but an object with a text property
        const xTitle = this.#chartState.layout.xaxis.title.text;
        const yTitle = this.#chartState.layout.yaxis.title.text;
        const range = this.#chartState.layout.xaxis.range;
        let reversedX = false;
        if (range.length > 1) {
            reversedX = range[0] > range[range.length - 1];
        }
        const plotSticks = typeof this.#chartState.data.error_y !== 'undefined';
        return {
            xyData,
            metadata: {
                xTitle,
                yTitle,
                'x.reverse': `${reversedX}`,
                'plot.style': plotSticks ? 'sticks' : 'line',
            },
        };
    }
    set data(data) {
        const xyData = DataChart.toXyArrays(data.xyData);
        this.#chartState.data.x = xyData.x;
        this.#chartState.data.y = xyData.y;
        this.#chartState.layout.xaxis.title.text = DataChart.#extractXAxisTitle(data.metadata);
        this.#chartState.layout.yaxis.title.text = DataChart.#extractYAxisTitle(data.metadata);
        if (data.metadata && data.metadata['x.reverse']) {
            delete this.#chartState.layout.xaxis.range;
            // see: https://plotly.com/javascript/axes/#reversed-axes
            this.#chartState.layout.xaxis.autorange = 'reversed';
        }
        else {
            delete this.#chartState.layout.xaxis.range;
            this.#chartState.layout.xaxis.autorange = true;
        }
        if (data.metadata &&
            data.metadata['plot.style'] &&
            data.metadata['plot.style'] === 'sticks') {
            // error bars as MS stick display, see:
            // https://plotly.com/javascript/line-and-scatter/
            // https://plotly.com/javascript/error-bars/
            // https://plotly.com/javascript/reference/scatter/
            // https://plotly.com/javascript/hover-text-and-formatting/
            this.#chartState.data.error_y = {
                type: 'data',
                symmetric: false,
                array: new Array(xyData.y.length).fill(0),
                arrayminus: xyData.y,
                thickness: 1,
                width: 0, // no marker on ends of error bar
            };
            this.#chartState.data.marker = { size: 1 };
            this.#chartState.data.hovertemplate =
                '(%{x:.f}, %{y:.f})<extra></extra>';
            this.#chartState.data.mode = 'none';
        }
        else {
            delete this.#chartState.data.error_y;
            delete this.#chartState.data.marker;
            delete this.#chartState.data.hovertemplate;
            this.#chartState.data.mode = 'lines';
        }
        this.render();
    }
    static #extractXAxisTitle(metadata) {
        if (metadata === null || typeof metadata === 'undefined') {
            return '';
        }
        const xLabel = Object.prototype.hasOwnProperty.call(metadata, 'x.label')
            ? metadata['x.label']
            : null;
        const xUnit = Object.prototype.hasOwnProperty.call(metadata, 'x.unit')
            ? metadata['x.unit']
            : null;
        let xTitle = '';
        if (xLabel !== null && xUnit !== null) {
            xTitle = `${xLabel} / ${xUnit}`;
        }
        else if (xLabel === null && xUnit !== null) {
            xTitle = `${xUnit}`;
        }
        else if (xLabel !== null && xUnit === null) {
            xTitle = `${xLabel}`;
        }
        return xTitle;
    }
    static #extractYAxisTitle(metadata) {
        if (metadata === null || typeof metadata === 'undefined') {
            return '';
        }
        const yLabel = Object.prototype.hasOwnProperty.call(metadata, 'y.label')
            ? metadata['y.label']
            : null;
        const yUnit = Object.prototype.hasOwnProperty.call(metadata, 'y.unit')
            ? metadata['y.unit']
            : null;
        let yTitle = '';
        if (yLabel !== null && yUnit !== null) {
            yTitle = `${yLabel} / ${yUnit}`;
        }
        else if (yLabel === null && yUnit !== null) {
            yTitle = `${yUnit}`;
        }
        else if (yLabel !== null && yUnit === null) {
            yTitle = `${yLabel}`;
        }
        return yTitle;
    }
    init() {
        if (!this.#initialized) {
            // avoid initial flash of incorrectly sized chart => hide
            this.classList.add('init');
            this.innerHTML = template;
            this.#chartContainer = this.querySelector('#sf-data-chart-placeholder');
            Plotly.newPlot(this.#chartContainer, [this.#chartState.data], this.#chartState.layout, this.#chartState.config);
            // initial resize to panel before 'responsive' config kicks in
            this.resize();
            this.#initialized = true;
            // unhide chart
            setTimeout(() => {
                this.classList.remove('init');
            }, 100);
        }
    }
    resize() {
        Plotly.Plots.resize(this.#chartContainer);
    }
    static toXyArrays(data) {
        const xArray = [];
        const yArray = [];
        if (data !== null && typeof data !== 'undefined') {
            for (const xyPair of data) {
                xArray.push(xyPair.x);
                yArray.push(xyPair.y);
            }
        }
        return { x: xArray, y: yArray };
    }
    static fromXyArrays(data) {
        const xyArray = [];
        for (let index = 0; index < data.x.length; index += 1) {
            const x = data.x[index];
            const y = data.y[index];
            xyArray.push({ x, y });
        }
        return xyArray;
    }
    render() {
        const mode = this.#chartState.data.mode !== 'none' &&
            this.#chartState.data.x.length > 1
            ? 'lines'
            : 'markers';
        this.#chartState.data.mode = mode;
        Plotly.newPlot(this.#chartContainer, [this.#chartState.data], this.#chartState.layout, this.#chartState.config);
    }
    handleDataChanged(message) {
        const url = new URL(message.detail.url);
        const sameUrl = isSameUrl(this.#url, url);
        if ((!sameUrl && message.name === nodeSelectedEvent) ||
            (sameUrl && message.name === nodeDataUpdatedEvent)) {
            this.#url = url;
            this.data = {
                xyData: message.detail.data,
                metadata: message.detail.metadata,
            };
        }
        else if (sameUrl && message.name === nodeDeselectedEvent) {
            this.#url = null;
            this.data = {
                xyData: [],
                metadata: {},
            };
        }
    }
    connectedCallback() {
        this.init();
        const handle0 = this.#channel.addListener(nodeSelectedEvent, this.handleDataChanged.bind(this));
        const handle1 = this.#channel.addListener(nodeDeselectedEvent, this.handleDataChanged.bind(this));
        const handle2 = this.#channel.addListener(nodeDataUpdatedEvent, this.handleDataChanged.bind(this));
        this.#handles.push(handle0, handle1, handle2);
        this.render();
    }
    disconnectedCallback() {
        for (const handle of this.#handles) {
            this.#channel.removeListener(handle);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    attributeChangedCallback(name, oldValue, newValue) {
        this.init();
    }
}
customElements.define('sf-data-chart', DataChart);
